import { Room } from 'matrix-js-sdk';
import { useMemo } from 'react';
import { FragmentType, getFragmentData, graphql } from '../../client/gql';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { RoomNames } from '../../utils/matrixClient';
import { organizationRolesAsString } from '../../utils/organizationRolesAsString';
import { RoomType } from '../constants';
import { useMatrixContext } from '../MatrixContext';
import { createProfileMap, mapMembersToProfiles, normalizeDisplayName } from '../utils/memberUtils';
import { useRooms } from './useRooms';

export type RoomInformation = {
  ownMatrixUserId: string;
  otherMatrixUserId: string;
  displayName: string;
  type: RoomNames;
  profile?: { name: string; uuid: string; roleName: string };
};

export type ExtendedRoom = Room & { roomInformation: RoomInformation };

export const UseDMRoomsFragment = graphql(`
  fragment UseDMRoomsFields on Query {
    profiles(where: { organization: { uuid_IN: $organizationUuids } }) {
      __typename  
      uuid
      organizationRoles
      organization {
          __typename
        uuid
        displayName
      }
    }
  }
`);

type UseDMRoomsProps = {
  data?: FragmentType<typeof UseDMRoomsFragment>;
};

export const useDMRooms = ({ data }: UseDMRoomsProps): ExtendedRoom[] => {
  const rooms = useRooms();

  const { client } = useMatrixContext();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const matrixUserId = client?.getUserId() || '';

  return useMemo(() => {
    const filteredRooms = rooms.filter((room) => {
      const roomType = room.name.split('_')[0];
      return roomType === RoomType.DM;
    });

    if (!data) {
      return filteredRooms.map((room) => {
        const extendedRoom = room as ExtendedRoom;
        extendedRoom.roomInformation = {
          ownMatrixUserId: matrixUserId,
          otherMatrixUserId: '',
          displayName: room.name,
          type: RoomNames.Unknown,
        };
        return extendedRoom;
      });
    }

    const fragment = getFragmentData(UseDMRoomsFragment, data);
    const profiles = fragment?.profiles;

    if (!profiles) {
      return filteredRooms.map((room) => {
        const extendedRoom = room as ExtendedRoom;
        extendedRoom.roomInformation = {
          ownMatrixUserId: matrixUserId,
          otherMatrixUserId: '',
          displayName: room.name,
          type: RoomNames.Unknown,
        };
        return extendedRoom;
      });
    }

    const profileMap = createProfileMap(profiles);

    return filteredRooms.map((room) => {
      const extendedRoom = room as ExtendedRoom;
      const roomMembers = room.getJoinedMembers();

      const parts = room.name.split('_');

      if (!roomMembers) {
        extendedRoom.roomInformation = {
          ownMatrixUserId: matrixUserId,
          otherMatrixUserId: '',
          displayName: room.name,
          type: RoomNames.Unknown,
        };
        return extendedRoom;
      }

      const membersMapping = mapMembersToProfiles(roomMembers);
      const other = membersMapping.find((m) => m.uuid !== profileUuid && m.displayName !== 'bp-backend');

      if (parts[0] === RoomNames.Direct && roomMembers.length > 0) {
        const profile1 = membersMapping.find((m) => m.uuid === parts[1]);
        const profile2 = membersMapping.find((m) => m.uuid === parts[2]);
        const profileToShow = profileUuid === profile1?.uuid ? profile2 : profile1;

        if (!profileToShow) {
          extendedRoom.roomInformation = {
            ownMatrixUserId: matrixUserId,
            otherMatrixUserId: other?.matrixUserId ?? '',
            displayName: room.name,
            type: RoomNames.Unknown,
          };
          return extendedRoom;
        }

        const fullProfileToShow = profileMap.get(profileToShow.uuid);
        const roleName =
          fullProfileToShow?.organization.uuid === organizationUuid || !fullProfileToShow?.organization.displayName
            ? organizationRolesAsString(fullProfileToShow?.organizationRoles ?? [])
            : fullProfileToShow.organization.displayName;

        extendedRoom.roomInformation = {
          ownMatrixUserId: matrixUserId,
          otherMatrixUserId: other?.matrixUserId ?? '',
          displayName: normalizeDisplayName(profileToShow.displayName),
          type: RoomNames.Direct,
          profile: {
            uuid: profileToShow.uuid,
            name: profileToShow.displayName,
            roleName,
          },
        };
        return extendedRoom;
      }

      extendedRoom.roomInformation = {
        ownMatrixUserId: matrixUserId,
        otherMatrixUserId: other?.matrixUserId ?? '',
        displayName: room.name,
        type: RoomNames.Unknown,
      };
      return extendedRoom;
    });
  }, [rooms, data, profileUuid, organizationUuid, matrixUserId]);
};
